import './style.css';

import type { CalendarListRef } from '@marceloterreiro/flash-calendar';
import {
  Calendar as FlashCalendar,
  fromDateId,
  toDateId,
  useDateRange,
} from '@marceloterreiro/flash-calendar';
import {
  addDays,
  addHours,
  addMonths,
  format,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isEqual,
  parseISO,
  set,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfDay,
} from 'date-fns';
import { useFocusEffect } from 'expo-router';
import { t } from 'i18next';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { generateTimes, isDesktop } from '@/core';
import { Button, Text } from '@/ui';

interface CalendarProps {
  mode?: 'datetime' | 'date';
  isVisible: boolean;
  minDate?: Date | null;
  maxDate?: Date;
  defaultStartDate?: Date | null;
  defaultEndDate?: Date | null;
  onHide: () => void;
  onDatesSelected: (startDatetime: Date, endDatetime: Date) => void;
  onDateSelected: (date: Date) => void;
  onTimeSelected: (time: Date) => void;
  range?: boolean;
  style?: any;
  id: string;
  headerTitle?: string;
  hasBackdrop?: boolean;
  coverScreen?: boolean;
  containerStyle?: any;
  overlayStyle?: any;
  showDesktopConfirm?: boolean;
  showDesktopOverview?: boolean;
  hideAfterTimeSelection?: boolean;
  onClear?: () => void;
  // Desktop only: render the calendar content as a plain positioned popover
  // instead of inside react-native-modal. Position it via `style`.
  inline?: boolean;
}

export const Calendar = ({
  mode = 'datetime',
  isVisible,
  minDate,
  maxDate,
  defaultStartDate,
  defaultEndDate,
  onHide,
  onDatesSelected,
  onDateSelected,
  onTimeSelected,
  range = true,
  style,
  id,
  headerTitle,
  hasBackdrop = true,
  coverScreen = true,
  containerStyle,
  overlayStyle,
  showDesktopConfirm = false,
  showDesktopOverview = false,
  hideAfterTimeSelection = true,
  onClear,
  inline = false,
}: CalendarProps) => {
  const { styles, theme } = useStyles(stylesheet);
  const isDark = UnistylesRuntime.themeName === 'dark';
  const ref = useRef<CalendarListRef>(null);
  const { calendarActiveDateRanges, onCalendarDayPress } = useDateRange({
    startId: toDateId(
      defaultStartDate || setHours(startOfDay(addDays(new Date(), 1)), 10)
    ),
    endId: toDateId(
      defaultEndDate || setHours(startOfDay(addDays(new Date(), 1)), 10)
    ),
  });
  const [selectedDate, setSelectedDate] = useState(
    toDateId(
      defaultStartDate || setHours(startOfDay(addDays(new Date(), 1)), 10)
    )
  );
  const [visible, setVisible] = React.useState(isVisible);
  const [startTime, setStartTime] = React.useState<Date>(
    defaultStartDate || setHours(startOfDay(addDays(new Date(), 1)), 10)
  );
  const [endTime, setEndTime] = React.useState<Date>(
    defaultEndDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
  );
  const scrollViewRef = useRef(null);

  useFocusEffect(() => {
    const indexToScrollTo = startTimeDatePickerTimes.findIndex(
      (t) => format(t, 'hh:mm a') === format(startTime, 'hh:mm a')
    );

    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({
        y: indexToScrollTo * 50,
        animated: false,
      });
    }
  });

  useEffect(() => {
    setVisible(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (defaultStartDate) {
      setStartTime(defaultStartDate);
      setSelectedDate(toDateId(defaultStartDate));

      if (range) {
        onCalendarDayPress(toDateId(defaultStartDate));
      }
    }
  }, [defaultStartDate]);

  useEffect(() => {
    if (defaultEndDate) {
      setEndTime(defaultEndDate);

      if (range) {
        onCalendarDayPress(toDateId(defaultEndDate));
      }
    }
  }, [defaultEndDate]);

  useEffect(() => {
    if (!range) return;

    if (calendarActiveDateRanges[0].startId) {
      const dateString = calendarActiveDateRanges[0].startId;
      const parsedDate = parseISO(dateString);

      const newStartDate = setMilliseconds(
        setSeconds(
          setMinutes(
            setHours(parsedDate, getHours(startTime)),
            getMinutes(startTime)
          ),
          0
        ),
        0
      );

      if (minDate && newStartDate < minDate) {
        setStartTime(minDate);
      }
    }
  }, [range, calendarActiveDateRanges, minDate, startTime]);

  const handleStartDateOnly = (date) => {
    setSelectedDate(date);
  };

  const startDate = useMemo(() => {
    if (!range && selectedDate) {
      return parseISO(selectedDate);
    }

    if (calendarActiveDateRanges[0].startId) {
      const dateString = calendarActiveDateRanges[0].startId;
      const parsedDate = parseISO(dateString);

      return parsedDate;
    }

    return null;
  }, [calendarActiveDateRanges, selectedDate, range]);

  const endDate = useMemo(() => {
    if (calendarActiveDateRanges[0].endId) {
      const dateString = calendarActiveDateRanges[0].endId;
      const parsedDate = parseISO(dateString);

      return parsedDate;
    }

    return null;
  }, [calendarActiveDateRanges]);

  const startDateFormatted = useMemo(() => {
    if (!range) {
      const parsedDate = parseISO(selectedDate);

      return parsedDate
        ? format(parsedDate, 'EEE, MMM dd yyyy')
        : 'Select Date';
    }

    return startDate ? format(startDate, 'EEE, MMM dd yyyy') : 'Select Date';
  }, [startDate, selectedDate, range]);

  const endDateFormatted = useMemo(() => {
    return endDate ? format(endDate, 'EEE, MMM dd yyyy') : 'Select Date';
  }, [endDate]);

  const startTimeFinal = useMemo(() => {
    if (!startDate || !startTime) return null;

    const newStartDate = setMilliseconds(
      setSeconds(
        setMinutes(
          setHours(startDate, getHours(startTime)),
          getMinutes(startTime)
        ),
        0
      ),
      0
    );

    return newStartDate;
  }, [startDate, startTime]);

  const endTimeFinal = useMemo(() => {
    if (!endDate || !endTime) return null;

    const newEndDate = setMilliseconds(
      setSeconds(
        setMinutes(setHours(endDate, getHours(endTime)), getMinutes(endTime)),
        0
      ),
      0
    );

    return newEndDate;
  }, [endDate, endTime]);

  const startTimeDatePickerTimes = useMemo(() => {
    if (!startTimeFinal) return [];

    let genTimes = generateTimes();

    if (!range && selectedDate) {
      const formattedSelectedDate = parseISO(selectedDate);

      genTimes = genTimes
        .map((time) =>
          set(time, {
            year: formattedSelectedDate.getFullYear(),
            month: formattedSelectedDate.getMonth(),
            date: formattedSelectedDate.getDate(),
            hours: time.getHours(),
            minutes: time.getMinutes(),
          })
        )
        .filter((time) => {
          const minDateCheck =
            !minDate || isAfter(time, minDate) || isEqual(time, minDate);

          const maxDateCheck =
            !maxDate || isBefore(time, maxDate) || isEqual(time, maxDate);

          return minDateCheck && maxDateCheck;
        });

      return genTimes;
    }

    genTimes = genTimes
      .map((time) => {
        return set(startTimeFinal, {
          year: startTimeFinal.getFullYear(),
          month: startTimeFinal.getMonth(),
          date: startTimeFinal.getDate(),
          hours: time.getHours(),
          minutes: time.getMinutes(),
        });
      })
      .filter((time) => {
        const minDateCheck =
          !minDate || isAfter(time, minDate) || isEqual(time, minDate);

        return minDateCheck;
      });

    return genTimes;
  }, [startTimeFinal, minDate, maxDate, selectedDate, range]);

  const endTimeDatePickerTimes = useMemo(() => {
    if (!startTimeFinal || !endTimeFinal) return [];

    let genTimes = generateTimes();

    const startTimePlusOneHour = addHours(startTimeFinal, 1);

    genTimes = genTimes
      .map((time) => {
        return set(endTimeFinal, {
          year: endTimeFinal.getFullYear(),
          month: endTimeFinal.getMonth(),
          date: endTimeFinal.getDate(),
          hours: time.getHours(),
          minutes: time.getMinutes(),
        });
      })
      .filter((time) => {
        const maxDateCheck =
          !maxDate || isBefore(time, maxDate) || isEqual(time, maxDate);

        return (
          (isAfter(time, startTimePlusOneHour) ||
            isEqual(time, startTimePlusOneHour)) &&
          maxDateCheck
        );
      });

    return genTimes;
  }, [maxDate, startTimeFinal, endTimeFinal]);

  useEffect(() => {
    if (!startTimeDatePickerTimes || !selectedDate || range) return;

    const parsedDate = new Date(selectedDate + 'T00:00:00');

    const newStartDate = setMilliseconds(
      setSeconds(
        setMinutes(
          setHours(parsedDate, getHours(startTime)),
          getMinutes(startTime)
        ),
        0
      ),
      0
    );

    const startValidTime = startTimeDatePickerTimes[0];
    const lastValidTime =
      startTimeDatePickerTimes[startTimeDatePickerTimes.length - 1];

    if (isBefore(newStartDate, startValidTime)) {
      setStartTime(startValidTime);
    } else if (isAfter(newStartDate, lastValidTime)) {
      setStartTime(lastValidTime);
    }
  }, [startTimeDatePickerTimes, selectedDate, startTime, range]);

  const TimeInput = forwardRef(({ value, onClick, className }, ref) => {
    return (
      <Button
        type="link"
        onPress={() => {
          onClick();
        }}
        style={{
          height: 30,
          marginLeft: 0,
          padding: 0,
          justifyContent: 'flex-start',
        }}
      >
        <Text style={{ color: theme.colors.button.link.default.text }}>
          {value}
        </Text>
      </Button>
    );
  });

  let handleColor = (time) => {
    return isDark ? 'dark-time-select' : 'time-select';
  };

  const onLoadCalendar = () => {
    setTimeout(() => {
      ref.current?.scrollToDate(
        range
          ? fromDateId(calendarActiveDateRanges[0].startId!)
          : fromDateId(selectedDate),
        false
      );
    }, 50);
  };

  const flashCalendarTheme = useMemo(
    () => ({
      itemDay: {
        active: () => ({
          container: {
            backgroundColor: isDark ? '#FFFFFF' : '#000000',
          },
          content: {
            color: isDark ? '#000000' : '#FFFFFF',
          },
        }),
      },
    }),
    [isDark]
  );

  // For the inline popover we keep the node mounted and toggle visibility via
  // opacity/pointerEvents (see below). Unmounting it leaves a stale box-shadow
  // "ghost" in Safari/WebKit, so only the modal path is allowed to unmount.
  if (!visible && !inline) return null;

  if (isDesktop()) {
    const desktopContent = (
      <View
        style={[styles.overlay, inline && styles.inlineOverlay, overlayStyle]}
      >
        <View style={[styles.desktopContainer, containerStyle]}>
          <View
            style={{
              height: '100%',
              width: mode === 'datetime' ? '50%' : '100%',
            }}
          >
            {headerTitle ? (
              <View
                style={{
                  width: '100%',
                  // The inline popover is a fixed-height box shared with the
                  // times column, so the heading has to stay slim to avoid
                  // eating into the month grid.
                  padding: inline ? 8 : 16,
                  marginBottom: inline ? 8 : 24,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                  }}
                >
                  <Text type={inline ? 'bodyBold' : 'title2'}>
                    {headerTitle}
                  </Text>

                  {/* The popover sits under its own field and closes on
                      outside click, so it doesn't need its own dismiss. */}
                  {inline ? null : (
                    <NavigationButton
                      type={NavigationButtonTypes.cancel}
                      onPress={() => {
                        setVisible(false);
                        onHide();
                      }}
                      style={{
                        position: 'absolute',
                        right: 0,
                      }}
                    />
                  )}
                </View>
              </View>
            ) : null}

            {range ? (
              <FlashCalendar.List
                ref={ref}
                calendarFutureScrollRangeInMonths={60}
                calendarPastScrollRangeInMonths={60}
                calendarRowHorizontalSpacing={12}
                calendarRowVerticalSpacing={16}
                calendarInstanceId={id}
                theme={flashCalendarTheme}
                calendarActiveDateRanges={calendarActiveDateRanges}
                calendarInitialMonthId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                calendarMaxDateId={toDateId(
                  maxDate || addMonths(new Date(), 12)
                )}
                calendarMinDateId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                onCalendarDayPress={onCalendarDayPress}
                onLoad={() => {
                  onLoadCalendar();
                }}
              />
            ) : (
              <FlashCalendar.List
                ref={ref}
                calendarFutureScrollRangeInMonths={60}
                calendarPastScrollRangeInMonths={60}
                calendarRowHorizontalSpacing={12}
                calendarRowVerticalSpacing={16}
                calendarInstanceId={id}
                theme={flashCalendarTheme}
                calendarActiveDateRanges={[
                  {
                    startId: selectedDate,
                    endId: selectedDate,
                  },
                ]}
                calendarInitialMonthId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                calendarMaxDateId={toDateId(
                  maxDate || addMonths(new Date(), 12)
                )}
                calendarMinDateId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                onCalendarDayPress={(dateId: string) => {
                  setSelectedDate(dateId);

                  const parsedDate = new Date(dateId + 'T00:00:00');

                  const newStartDate = setMilliseconds(
                    setSeconds(
                      setMinutes(
                        setHours(parsedDate, getHours(startTime)),
                        getMinutes(startTime)
                      ),
                      0
                    ),
                    0
                  );

                  onDateSelected(newStartDate);
                }}
                numColumns={1}
                onLoad={() => {
                  onLoadCalendar();
                }}
              />
            )}

            {showDesktopOverview ? (
              <View
                style={{
                  width: '100%',
                  padding: 16,
                  paddingTop: 24,
                  gap: 16,
                  borderTopColor: theme.colors.card.default.borderColor,
                  borderTopWidth: 1,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <View
                    style={{
                      alignItems: 'center',
                      flexGrow: range ? 0 : 1,
                    }}
                  >
                    <Text type="bodyRegular" style={{ height: 22 }}>
                      {startDateFormatted}
                    </Text>

                    {mode === 'datetime' ? (
                      <DatePicker
                        selected={startTime}
                        onChange={(d) => {
                          setStartTime(d);

                          const newStartDate = setMilliseconds(
                            setSeconds(
                              setMinutes(
                                setHours(startDate, getHours(d)),
                                getMinutes(d)
                              ),
                              0
                            ),
                            0
                          );

                          onTimeSelected(newStartDate);
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={30}
                        includeTimes={startTimeDatePickerTimes}
                        timeCaption="Time"
                        dateFormat="h:mm aa"
                        customInput={<TimeInput />}
                        timeClassName={handleColor}
                        showPopperArrow={false}
                        dropdownMode="scroll"
                        popperClassName="datepicker-popper"
                        calendarClassName={
                          isDark ? 'dark-calendar' : 'calendar'
                        }
                      />
                    ) : null}
                  </View>

                  {range && (
                    <View style={{ alignItems: 'center' }}>
                      <Text
                        type="bodyRegular"
                        style={{ height: 22, width: '100%' }}
                      >
                        {endDateFormatted}
                      </Text>

                      {mode === 'datetime' ? (
                        <DatePicker
                          selected={endTime}
                          onChange={(d) => {
                            setEndTime(d);
                          }}
                          showTimeSelect
                          showTimeSelectOnly
                          dateFormat="h:mm aa"
                          timeIntervals={30}
                          includeTimes={endTimeDatePickerTimes}
                          customInput={<TimeInput />}
                          timeClassName={handleColor}
                          showPopperArrow={false}
                          dropdownMode="scroll"
                          popperClassName="datepicker-popper"
                          calendarClassName={
                            isDark ? 'dark-calendar' : 'calendar'
                          }
                        />
                      ) : null}
                    </View>
                  )}
                </View>

                <View
                  style={{
                    alignItems: 'flex-end',
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 16,
                    }}
                  >
                    <Button
                      type="ghost"
                      text="Clear"
                      style={{ width: 120 }}
                      onPress={() => {
                        onClear?.();
                      }}
                    />

                    <Button
                      text="Confirm"
                      type="primary"
                      style={{ width: 120 }}
                      onPress={() => {
                        if (!range) {
                          const newStartDate = setMilliseconds(
                            setSeconds(
                              setMinutes(
                                setHours(startDate, getHours(startTime)),
                                getMinutes(startTime)
                              ),
                              0
                            ),
                            0
                          );

                          onDateSelected(newStartDate);
                          onDatesSelected?.(newStartDate, newStartDate);
                          onHide();
                          return;
                        }

                        if (startDate && endDate) {
                          const newStartDate = setMilliseconds(
                            setSeconds(
                              setMinutes(
                                setHours(startDate, getHours(startTime)),
                                getMinutes(startTime)
                              ),
                              0
                            ),
                            0
                          );

                          const newEndDate = setMilliseconds(
                            setSeconds(
                              setMinutes(
                                setHours(endDate, getHours(endTime)),
                                getMinutes(endTime)
                              ),
                              0
                            ),
                            0
                          );

                          onDatesSelected(newStartDate, newEndDate);
                          onHide();
                        }
                      }}
                    />
                  </View>
                </View>
              </View>
            ) : null}
          </View>

          {mode === 'datetime' ? (
            <View style={{ width: '50%', height: '100%' }}>
              <Text type="title2" style={{ alignSelf: 'center' }}>
                Available Times
              </Text>

              <ScrollView ref={scrollViewRef}>
                <View style={{ paddingLeft: 16, paddingRight: 16 }}>
                  {startTimeDatePickerTimes.map((time) => (
                    <Button
                      key={time.toString()}
                      type="link"
                      onPress={() => {
                        setStartTime(time);

                        const newStartDate = setMilliseconds(
                          setSeconds(
                            setMinutes(
                              setHours(startDate, getHours(time)),
                              getMinutes(time)
                            ),
                            0
                          ),
                          0
                        );

                        onTimeSelected(newStartDate);

                        if (hideAfterTimeSelection) {
                          onHide();
                        }
                      }}
                      style={{
                        height: 50,
                        padding: 0,
                        margin: 0,
                        ...(format(startTime, 'hh:mm a') ===
                          format(time, 'hh:mm a') && {
                          borderWidth: 1,
                          backgroundColor: theme.colors.calendar.selected.time,
                          borderColor:
                            theme.colors.calendar.selected.borderTime,
                        }),
                      }}
                    >
                      <Text type="bodyRegular">{format(time, 'hh:mm a')}</Text>
                    </Button>
                  ))}
                </View>
              </ScrollView>
            </View>
          ) : null}
        </View>

        {showDesktopConfirm ? (
          <View style={{ alignItems: 'center' }}>
            <Button
              text="Choose Date"
              type="primary"
              style={{ width: '90%' }}
              onPress={() => {
                if (startDate) {
                  const newStartDate = setMilliseconds(
                    setSeconds(
                      setMinutes(
                        setHours(startDate, getHours(startTime)),
                        getMinutes(startTime)
                      ),
                      0
                    ),
                    0
                  );

                  onDatesSelected(newStartDate, newStartDate);

                  onHide();
                }
              }}
            />
          </View>
        ) : null}
      </View>
    );

    if (inline) {
      // Stay mounted and hide via opacity instead of unmounting. Removing the
      // shadowed node makes Safari leave a ghost of the drop shadow behind;
      // toggling opacity repaints the layer cleanly with nothing left over.
      return (
        <View
          style={[
            style,
            {
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? 'auto' : 'none',
            },
          ]}
          aria-hidden={!visible}
          id={id}
        >
          {desktopContent}
        </View>
      );
    }

    return (
      <View style={style} id={id}>
        <Modal
          animationIn="slideInUp"
          animationInTiming={1}
          animationOut="slideOutDown"
          animationOutTiming={1}
          isVisible={visible}
          hasBackdrop={hasBackdrop}
          onModalHide={() => {
            onHide();
          }}
          onBackdropPress={() => {
            onHide();
          }}
          coverScreen={coverScreen}
        >
          {desktopContent}
        </Modal>
      </View>
    );
  }

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={1000}
      isVisible={visible}
      hasBackdrop
      onModalHide={() => {
        onHide();
      }}
      onBackdropPress={() => {
        onHide();
      }}
      style={{ margin: 0 }}
    >
      <View style={styles.overlay}>
        <View style={styles.mobileContainer}>
          <View
            style={{
              width: '100%',
              padding: 16,
              marginBottom: 24,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <Text type="title2">
                {headerTitle ||
                  (range
                    ? t('calendar.headerStartReturn')
                    : t('calendar.headerStart'))}
              </Text>

              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={() => {
                  setVisible(false);
                  onHide();
                }}
                style={{
                  position: 'absolute',
                  right: 0,
                }}
              />
            </View>
          </View>

          <View
            style={{
              width: '100%',
              flex: 1,
            }}
          >
            {range ? (
              <FlashCalendar.List
                ref={ref}
                calendarFutureScrollRangeInMonths={60}
                calendarPastScrollRangeInMonths={60}
                calendarRowHorizontalSpacing={12}
                calendarRowVerticalSpacing={16}
                calendarInstanceId={id}
                theme={flashCalendarTheme}
                calendarActiveDateRanges={calendarActiveDateRanges}
                calendarInitialMonthId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                calendarMaxDateId={toDateId(
                  maxDate || addMonths(new Date(), 12)
                )}
                calendarMinDateId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                onCalendarDayPress={onCalendarDayPress}
                onLoad={() => {
                  onLoadCalendar();
                }}
              />
            ) : (
              <FlashCalendar.List
                ref={ref}
                calendarFutureScrollRangeInMonths={60}
                calendarPastScrollRangeInMonths={60}
                calendarRowHorizontalSpacing={12}
                calendarRowVerticalSpacing={16}
                calendarInstanceId={id}
                theme={flashCalendarTheme}
                calendarActiveDateRanges={[
                  {
                    startId: selectedDate,
                    endId: selectedDate,
                  },
                ]}
                calendarInitialMonthId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                calendarMaxDateId={toDateId(
                  maxDate || addMonths(new Date(), 12)
                )}
                calendarMinDateId={toDateId(
                  minDate || setHours(startOfDay(addDays(new Date(), 2)), 10)
                )}
                onCalendarDayPress={handleStartDateOnly}
                numColumns={1}
                onLoad={() => {
                  onLoadCalendar();
                }}
              />
            )}
          </View>

          <View
            style={{
              width: '100%',
              padding: 16,
              paddingTop: 24,
              gap: 16,
              borderTopColor: theme.colors.card.default.borderColor,
              borderTopWidth: 1,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <View style={{ alignItems: 'center', flexGrow: range ? 0 : 1 }}>
                <Text type="bodyRegular" style={{ height: 22 }}>
                  {startDateFormatted}
                </Text>

                {mode === 'datetime' ? (
                  <DatePicker
                    selected={startTime}
                    onChange={(d) => {
                      setStartTime(d);

                      const newStartDate = setMilliseconds(
                        setSeconds(
                          setMinutes(
                            setHours(startDate, getHours(d)),
                            getMinutes(d)
                          ),
                          0
                        ),
                        0
                      );

                      onTimeSelected(newStartDate);
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    includeTimes={startTimeDatePickerTimes}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    customInput={<TimeInput />}
                    timeClassName={handleColor}
                    showPopperArrow={false}
                    dropdownMode="scroll"
                    popperClassName="datepicker-popper"
                    calendarClassName={isDark ? 'dark-calendar' : 'calendar'}
                  />
                ) : null}
              </View>

              {range && (
                <View style={{ alignItems: 'center' }}>
                  <Text
                    type="bodyRegular"
                    style={{ height: 22, width: '100%' }}
                  >
                    {endDateFormatted}
                  </Text>

                  {mode === 'datetime' ? (
                    <DatePicker
                      selected={endTime}
                      onChange={(d) => {
                        setEndTime(d);
                      }}
                      showTimeSelect
                      showTimeSelectOnly
                      dateFormat="h:mm aa"
                      timeIntervals={30}
                      includeTimes={endTimeDatePickerTimes}
                      customInput={<TimeInput />}
                      timeClassName={handleColor}
                      showPopperArrow={false}
                      dropdownMode="scroll"
                      popperClassName="datepicker-popper"
                      calendarClassName={isDark ? 'dark-calendar' : 'calendar'}
                    />
                  ) : null}
                </View>
              )}
            </View>

            <View style={{ alignItems: 'center' }}>
              <Button
                text="Confirm"
                type="primary"
                onPress={() => {
                  if (!range) {
                    const newStartDate = setMilliseconds(
                      setSeconds(
                        setMinutes(
                          setHours(startDate, getHours(startTime)),
                          getMinutes(startTime)
                        ),
                        0
                      ),
                      0
                    );

                    onDateSelected(newStartDate);
                    onDatesSelected?.(newStartDate, newStartDate);
                    onHide();
                    return;
                  }

                  if (startDate && endDate) {
                    const newStartDate = setMilliseconds(
                      setSeconds(
                        setMinutes(
                          setHours(startDate, getHours(startTime)),
                          getMinutes(startTime)
                        ),
                        0
                      ),
                      0
                    );

                    const newEndDate = setMilliseconds(
                      setSeconds(
                        setMinutes(
                          setHours(endDate, getHours(endTime)),
                          getMinutes(endTime)
                        ),
                        0
                      ),
                      0
                    );

                    onDatesSelected(newStartDate, newEndDate);
                    onHide();
                  }
                }}
                style={{ width: '90%' }}
              />

              {onClear ? (
                <Button
                  type="ghost"
                  text="Clear"
                  style={{ width: 120 }}
                  onPress={() => {
                    onClear?.();
                  }}
                />
              ) : null}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  overlay: {
    margin: 'auto',
    backgroundColor: theme.colors.card.default.backgroundColor,
    maxHeight: {
      md: 800,
    },
    maxWidth: {
      sm: 600,
    },
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  inlineOverlay: {
    margin: 0,
    width: 'auto',
    height: 'auto',
    maxHeight: undefined,
    maxWidth: undefined,
  },
  mobileContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 16,
    padding: 16,
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.card.default.backgroundColor,
  },
  desktopContainer: {
    flexDirection: 'row',
    borderRadius: 16,
    padding: 24,
    width: 600,
    height: 400,
    backgroundColor: theme.colors.card.default.backgroundColor,
  },
}));
